import { useApi } from "@/hooks/useApi";
import { PROMPT_EVENTS } from "@/lib/prompt-events";
import { PromptDTO, UserPromptsPreferencesDTO } from "@shaarp/common";
import { useCallback, useEffect, useRef, useState } from "react";

type UsePromptPreferencesBaseOptions = {
  fetchCustomPrompts?: boolean;
  listenForChanges?: boolean;
  onPreferencesFetched: (data: UserPromptsPreferencesDTO) => void;
};

type UsePromptPreferencesBaseReturn = {
  customPrompts: PromptDTO[];
  isLoadingPreferences: boolean;
  isLoadingPrompts: boolean;
  fetchPreferences: () => Promise<void>;
};

/**
 * Base hook that encapsulates shared logic for prompt preference hooks:
 * - Stable apiRef pattern to avoid effect loops from fresh axios instances
 * - Preference fetching via /prompts/preferences/me
 * - Optional custom prompts loading via /prompts
 * - Optional PROMPTS_CHANGED event listener that refreshes both
 */
export function usePromptPreferencesBase(
  options: UsePromptPreferencesBaseOptions,
): UsePromptPreferencesBaseReturn {
  const {
    fetchCustomPrompts = false,
    listenForChanges = true,
    onPreferencesFetched,
  } = options;

  const api = useApi();
  // useApi() returns a fresh axios instance each render — keep a ref to a stable
  // reference so deps arrays don't fire effects in a loop.
  const apiRef = useRef(api);
  apiRef.current = api;

  const [customPrompts, setCustomPrompts] = useState<PromptDTO[]>([]);
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(true);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(fetchCustomPrompts);

  const fetchPreferences = useCallback(async () => {
    try {
      const { data } = await apiRef.current.get<UserPromptsPreferencesDTO>(
        "/prompts/preferences/me",
      );
      onPreferencesFetched(data);
    } catch {
      // 401 = session expired, Clerk handles redirect — ignore silently
    }
  }, [onPreferencesFetched]);

  // Initial fetch of preferences — re-runs when fetchPreferences changes (e.g. context change)
  useEffect(() => {
    const init = async () => {
      setIsLoadingPreferences(true);
      try {
        await fetchPreferences();
      } finally {
        setIsLoadingPreferences(false);
      }
    };
    init();
  }, [fetchPreferences]);

  // Fetch custom prompts if needed
  useEffect(() => {
    if (!fetchCustomPrompts) {
      setIsLoadingPrompts(false);
      return;
    }

    const fetchPrompts = async () => {
      setIsLoadingPrompts(true);
      try {
        const { data } = await apiRef.current.get<PromptDTO[]>("/prompts");
        setCustomPrompts(data);
      } catch {
        // 401 = session expired, Clerk handles redirect
      } finally {
        setIsLoadingPrompts(false);
      }
    };

    fetchPrompts();
  }, [fetchCustomPrompts]);

  // Listen for prompts changes from other components (creation, update, deletion)
  useEffect(() => {
    if (!listenForChanges) return;

    const handlePromptsChanged = async () => {
      try {
        // Refresh preferences to sync ids (in case a prompt was deleted)
        await fetchPreferences();

        // Also refresh custom prompts if needed
        if (fetchCustomPrompts) {
          const { data } = await apiRef.current.get<PromptDTO[]>("/prompts");
          setCustomPrompts(data);
        }
      } catch {
        // 401 = session expired, Clerk handles redirect
      }
    };

    window.addEventListener(PROMPT_EVENTS.PROMPTS_CHANGED, handlePromptsChanged);
    return () => {
      window.removeEventListener(
        PROMPT_EVENTS.PROMPTS_CHANGED,
        handlePromptsChanged,
      );
    };
  }, [fetchCustomPrompts, listenForChanges, fetchPreferences]);

  return {
    customPrompts,
    isLoadingPreferences,
    isLoadingPrompts,
    fetchPreferences,
  };
}
